<template>
  <div class="fan fan-component f-plugin-devmap">
    <!-- 设备类别选择 -->
    <div class="top-bar">
      <a-select v-model="selectedType" placeholder="选择设备类别" style="width:220px;" allow-clear @change="onTypeChange">
        <a-option v-for="dt in deviceTypes" :key="dt.id" :value="dt.id" :label="dt.name" />
      </a-select>

      <!-- 动态查询条件 -->
      <template v-for="col in searchColumns" :key="col.dataIndex">
        <a-select
          v-if="col.form === 'select'"
          v-model="searchValues[col.dataIndex]"
          :placeholder="'选择' + col.title"
          style="width:160px;"
          allow-clear
          @change="onSearchChange"
        >
          <a-option v-for="(opt, idx) in getStoreOptions(col.dataIndex)" :key="idx" :value="opt.value" :label="opt.label" />
        </a-select>
        <a-input
          v-else-if="col.form === 'input'"
          v-model="searchValues[col.dataIndex]"
          :placeholder="'输入' + col.title"
          style="width:160px;"
          allow-clear
          @input="onSearchInput"
        />
      </template>

      <span class="tip">拖拽地图上的标记可调整设备位置</span>
    </div>

    <!-- 平面图 -->
    <a-spin :loading="loading">
      <div class="map-area" v-if="currentImage">
        <IndoorMarker ref="indoorMarker"
                      width="100%"
                      height="auto"
                      :value="markerBlocks"
                      :backgroundImage="{ url: currentImage, width: '100%', height: 'auto' }"
                      @dragEnd="onMarkerDragEnd" />
      </div>
      <div v-else class="empty-tip">请选择设备类别查看平面图</div>
    </a-spin>

    <!-- 设备列表 -->
    <div v-if="devices.length > 0" class="device-list">
      <a-divider />
      <div class="list-header">
        <span class="list-title">设备列表</span>
        <a-button type="primary" size="small" :loading="savingAll" @click="saveAll">一键全部保存</a-button>
      </div>
      <a-table :data="devices" :pagination="false" row-key="id" size="small" :row-class="rowClass">
        <template #columns>
          <a-table-column title="设备名称" data-index="name" />
          <a-table-column title="X%" :width="80" align="center">
            <template #cell="{ record }">{{ getPoints(record).x }}</template>
          </a-table-column>
          <a-table-column title="Y%" :width="80" align="center">
            <template #cell="{ record }">{{ getPoints(record).y }}</template>
          </a-table-column>
          <a-table-column title="操作" :width="80" align="center">
            <template #cell="{ record }">
              <a-button size="mini" type="primary" :loading="record._saving" :disabled="!record._changed" @click="saveOne(record)">保存</a-button>
            </template>
          </a-table-column>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import IndoorMarker from '@/companents/parts/IndoorMarker.vue'

export default {
  name: 'FPluginDevMap',
  components: { IndoorMarker },
  props: {
    superApi: { type: String, default: 'device' },
    superTitle: { type: String, default: '' },
    superIdDevicetype: { type: [Number, String], default: '' },
    typeApi: { type: String, default: 'devicetype' },
    typeImageField: { type: String, default: 'image' },
    filterField: { type: String, default: 'id_devicetype' },
    pointsField: { type: String, default: 'points' },
    saveField: { type: String, default: '' },
    queryParams: { type: Object, default: () => ({}) },
    columns: { type: Array, default: () => ([]) }
  },
  data() {
    return {
      deviceTypes: [],
      selectedType: '',
      currentImage: '',
      loading: false,
      devices: [],
      activeId: '',
      savingAll: false,
      searchValues: {}
    }
  },
  computed: {
    markerBlocks() {
      return this.devices.map(d => {
        const pt = this.getPoints(d)
        return { id: d.id, name: d.name, x: pt.x, y: pt.y }
      })
    },
    searchColumns() {
      if (!this.columns || !this.columns.length) return []
      return this.columns.filter(col => col.search && col.dataIndex)
    }
  },
  methods: {
    // 从 store 获取选项（loadCallback 加载后存入 store.state.page.options）
    getStoreOptions(field) {
      const storeOpts = this.$store?.state?.page?.options
      return (storeOpts && storeOpts[field]) || []
    },

    getPoints(record) {
      const pf = this.pointsField
      let pt = record[pf]
      if (typeof pt === 'string') { try { pt = JSON.parse(pt) } catch { pt = {} } }
      return { x: pt?.x != null ? pt.x : 50, y: pt?.y != null ? pt.y : 50 }
    },

    setPoints(record, x, y) {
      const pf = this.pointsField
      record[pf] = { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 }
    },

    rowClass(record) { return record.id === this.activeId ? 'row-active' : '' },

    // 使用 $fan.page.loadCallback 加载 columns 选项（extendTable / extendOption）
    async loadColumnOptions() {
      if (!this.columns || !this.columns.length) return
      try {
        await this.$fan.page.loadCallback(this.columns, {})
      } catch (e) {
        console.warn('加载 columns 选项失败:', e)
      }
    },

    onSearchChange() {
      if (this.selectedType) {
        this.loadDevices(this.selectedType)
      }
    },

    onSearchInput() {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        if (this.selectedType) {
          this.loadDevices(this.selectedType)
        }
      }, 500)
    },

    buildQueryParams() {
      const params = { ...this.queryParams }
      this.searchColumns.forEach(col => {
        const field = col.dataIndex
        const val = this.searchValues[field]
        if (val !== undefined && val !== '' && val !== null) {
          params[field] = val
        }
      })
      return params
    },

    async loadDeviceTypes() {
      try {
        const res = await this.$fan.api.get(this.typeApi, { _tree: 'true', page: 1, limit: 999, ...this.queryParams })
        if (res && res.status === 0) {
          const imgField = this.typeImageField
          const flat = (arr) => arr.reduce((acc, item) => {
            if (!item.children || item.children.length === 0) {
              acc.push({ id: item.id, name: item.name, image: item[imgField] || '' })
            }
            if (item.children) acc.push(...flat(item.children))
            return acc
          }, [])
          this.deviceTypes = flat(res.data?.data || res.data || [])
        }
      } catch {}
    },

    async onTypeChange(val) {
      if (!val) { this.currentImage = ''; this.devices = []; return }
      const dt = this.deviceTypes.find(d => d.id == val)
      this.currentImage = dt ? (dt[this.typeImageField] || '') : ''
      await this.loadDevices(val)
    },

    async loadDevices(typeId) {
      this.loading = true
      try {
        const query = { page: 1, limit: 999, ...this.buildQueryParams() }
        query[this.filterField] = typeId
        const res = await this.$fan.api.get(this.superApi, query)
        if (res && res.status === 0) {
          const list = res.data?.data || res.data || []
          this.devices = list.map(d => ({
            ...d,
            _changed: false,
            _saving: false
          }))
        }
      } catch {
        this.$message.error('加载设备列表失败')
      } finally { this.loading = false }
    },

    onMarkerDragEnd(block) {
      const dev = this.devices.find(d => d.id == block.id)
      if (dev) {
        this.setPoints(dev, block.x, block.y)
        dev._changed = true
        this.activeId = dev.id
      }
    },

    getSaveFieldValue(record) {
      const pt = this.getPoints(record)
      return `(${pt.x},${pt.y})`
    },

    async saveAll() {
      const changed = this.devices.filter(d => d._changed)
      if (!changed.length) { this.$message.info('没有需要保存的更改'); return }
      this.savingAll = true
      let ok = 0
      const saveFld = this.saveField || this.pointsField
      for (const d of changed) {
        try {
          const res = await this.$fan.api.put(this.superApi + '/' + d.id, { [saveFld]: this.getSaveFieldValue(d) })
          if (res && res.status === 0) { d._changed = false; ok++ }
        } catch {}
      }
      this.savingAll = false
      this.$message.success(`已保存 ${ok}/${changed.length} 项`)
    },

    async saveOne(record) {
      record._saving = true
      const saveFld = this.saveField || this.pointsField
      try {
        const res = await this.$fan.api.put(this.superApi + '/' + record.id, { [saveFld]: this.getSaveFieldValue(record) })
        if (res && res.status === 0) { record._changed = false }
        else { this.$message.error('保存失败') }
      } catch { this.$message.error('保存失败') }
      finally { record._saving = false }
    }
  },

  async mounted() {
    await this.loadColumnOptions()
    await this.loadDeviceTypes()
    if (this.superIdDevicetype) {
      const dt = this.deviceTypes.find(d => d.id == this.superIdDevicetype)
      if (dt) {
        this.selectedType = dt.id
        this.currentImage = (dt[this.typeImageField] || '')
        await this.loadDevices(dt.id)
      }
    }
  }
}
</script>

<style scoped lang="less">
.f-plugin-devmap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.tip {
  color: #86909c;
  font-size: 13px;
}

.map-area {
  min-height: 300px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.empty-tip {
  text-align: center;
  color: #c9cdd4;
  padding: 200px 0;
  font-size: 16px;
}

.device-list {
  flex: 1;
  overflow: auto;
  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .list-title { font-weight: 600; }
  :deep(.row-active) { background: #e8f3ff !important; }
}
</style>
